import React from "react";
import styles from "./LastSessionCard.module.scss";
import { useGetLastSession } from "@/hooks/devourplay/useGetLastSession";
import moment from "moment";
import Skeleton from "@/components/skeletons/Skeleton";
import { IoGameControllerOutline } from "react-icons/io5";
import { FiClock, FiChevronDown, FiChevronUp } from "react-icons/fi";
import useGetDevourPlayUserStats from "@/hooks/useGetDevourPlayUserStats";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import AnimateHeight from "react-animate-height";
import {displayGameTitle} from "@/utils/displayGameTitle";

interface LastSessionCardProps {
    defaultExpanded?: boolean;
    forceExpanded?: boolean;
}

export const LastSessionCard: React.FC<LastSessionCardProps> = ({ defaultExpanded = false, forceExpanded = false }) => {
    const { data, isLoading, error } = useGetLastSession();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { data: userStats } = useGetDevourPlayUserStats(fullToken, undefined, !!fullToken);

    // Clear old localStorage on mount (one-time migration)
    React.useEffect(() => {
        localStorage.removeItem("cockpit-preferences");
    }, []);

    // forceExpanded bypasses local state entirely (used in full view mode)
    const [localExpanded, setLocalExpanded] = React.useState(forceExpanded || defaultExpanded);
    const expanded = forceExpanded || localExpanded;

    // Find the weekly time quest stat
    const weeklyTimeQuest = userStats?.userStats?.find(stat =>
        stat.weeklyProgress !== undefined && stat.rewardCategory === "TIME");
    const weeklyXP = weeklyTimeQuest?.weeklyProgress || 0;

    const formatDuration = (minutes: number) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return `${hours}h ${mins}m`;
        }
        return `${mins}m`;
    };

    const formatTimeAgo = (timestamp: string) => {
    // Customize moment's relative time to use abbreviations
        const time = moment(timestamp).fromNow();
        return time
            .replace(" hours", " hr")
            .replace(" hour", " hr")
            .replace(" minutes", " min")
            .replace(" minute", " min")
            .replace(" months", " mo")
            .replace(" month", " mo")
            .replace(" days", " d")
            .replace(" day", " d")
            .replace(" years", " yr")
            .replace(" year", " yr")
            .replace("an ", "1 ")
            .replace("a ", "1 ");
    };

    const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
        // Don't allow toggling when forceExpanded is true
        if (forceExpanded) return;
        if (e.type === "click" || (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === " ") {
            /*
             * preventDefault only for the keys we actually handle. It used to
             * run unconditionally, and because onKeyDown fires for EVERY key,
             * that cancelled Tab's default focus move — a keyboard user who
             * reached this header could not Tab back off it.
             */
            e.preventDefault();
            setLocalExpanded(!localExpanded);
        }
    };

    if (isLoading) {
        return (
            <div className={styles.lastSessionCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <IoGameControllerOutline />
                        </span>
                        <h3>Gaming Sessions</h3>
                    </div>
                </div>
                <div className={styles.content}>
                    <Skeleton height={60} rows={1} />
                    <Skeleton height={20} width="60%" rows={1} classes={styles.skeletonMargin} />
                </div>
            </div>
        );
    }

    /*
     * An error is NOT an empty state. These used to share one branch, so a
     * player with hundreds of logged hours who hit a 500 was told "Ready to
     * Start Gaming? ... once you start playing" — the app confidently
     * asserting they had never played.
     */
    if (error) {
        return (
            <div className={styles.lastSessionCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <IoGameControllerOutline />
                        </span>
                        <h3>Gaming Sessions</h3>
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.noSession}>
                        <h4>Couldn&apos;t load your sessions</h4>
                        <p>Something went wrong on our end. Your play history is safe — refresh to try again.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className={styles.lastSessionCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <IoGameControllerOutline />
                        </span>
                        <h3>Gaming Sessions</h3>
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.noSession}>
                        <div className={styles.emptyIcon}>🎮</div>
                        <h4>Ready to Start Gaming?</h4>
                        <p>Your gaming sessions and weekly time XP will appear here once you start playing.</p>
                    </div>
                </div>
            </div>
        );
    }

    const { lastSession, todaySummary, recentSessions } = data;

    if (!lastSession) {
        return (
            <div className={styles.lastSessionCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <IoGameControllerOutline />
                        </span>
                        <h3>Gaming Sessions</h3>
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.noSession}>
                        <div className={styles.emptyIcon}>🎮</div>
                        <h4>Ready to Start Gaming?</h4>
                        <p>Your gaming sessions and weekly time XP will appear here once you start playing.</p>
                    </div>
                </div>
            </div>
        );
    }


    return (
        <div className={`${styles.lastSessionCard} ${expanded ? styles.expanded : styles.collapsed}`}>
            <div
                className={styles.header}
                onClick={handleToggle}
                onKeyDown={handleToggle}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-controls="last-session-content"
            >
                <div className={styles.titleGroup}>
                    <span className={styles.iconWrapper}>
                        <IoGameControllerOutline />
                    </span>
                    <h3>Gaming Sessions</h3>
                </div>
                <button
                    className={styles.collapseToggle}
                    aria-label={expanded ? "Collapse" : "Expand"}
                >
                    {expanded ? <FiChevronUp /> : <FiChevronDown />}
                </button>
            </div>

            <AnimateHeight
                id="last-session-content"
                duration={300}
                height={expanded ? "auto" : 0}
            >
                <div className={styles.content}>
                    {/* Weekly Time XP - Simplified */}
                    <div className={styles.weeklyXPSimple}>
                        <div className={styles.xpRow}>
                            <span className={styles.label}>Your Weekly XP:</span>
                            <span className={styles.value}>
                                {weeklyXP} XP
                            </span>
                        </div>
                        <div className={styles.xpLimit}>
                            <span className={styles.limitText}>Earn 10XP/hour | Max 250XP/week</span>
                        </div>
                    </div>

                    {/* Recent Sessions - Now the main list */}
                    <div className={styles.recentSessions}>
                        <h5>Recent Sessions:</h5>
                        <div className={styles.sessionsTable}>
                            {/* Show last 5 sessions including the most recent */}
                            {recentSessions && recentSessions.length > 0
                                ? recentSessions.slice(0, 5).map((session, index) =>
                                    <div key={session.sessionId || index} className={styles.sessionRow}>
                                        <div className={styles.gameInfo}>
                                            <IoGameControllerOutline className={styles.gameIcon} />
                                            <span className={styles.gameTitle}>{displayGameTitle(session.gameTitle)}</span>
                                        </div>
                                        <div className={styles.sessionStats}>
                                            <span className={styles.duration}>
                                                <FiClock className={styles.clockIcon} />
                                                {formatDuration(session.duration)}
                                            </span>
                                            <span className={styles.timeAgo}>{formatTimeAgo(session.timestamp)}</span>
                                        </div>
                                    </div>)
                                : lastSession &&
              <div className={styles.sessionRow}>
                  <div className={styles.gameInfo}>
                      <IoGameControllerOutline className={styles.gameIcon} />
                      <span className={styles.gameTitle}>{displayGameTitle(lastSession.gameTitle)}</span>
                  </div>
                  <div className={styles.sessionStats}>
                      <span className={styles.duration}>
                          <FiClock className={styles.clockIcon} />
                          {formatDuration(lastSession.sessionDuration)}
                      </span>
                      <span className={styles.timeAgo}>{formatTimeAgo(lastSession.lastPlayed)}</span>
                  </div>
              </div>
                            }
                        </div>
                    </div>

                    {/* Today's Summary - Focus on play time */}
                    {todaySummary && todaySummary.totalTime > 0 &&
          <div className={styles.todaySummary}>
              <h5>Today's Playtime</h5>
              <div className={styles.summaryStats}>
                  <div className={styles.stat}>
                      <span className={styles.value}>
                          {Math.floor(todaySummary.totalTime / 60)}h {todaySummary.totalTime % 60}m
                      </span>
                      <span className={styles.label}>Total</span>
                  </div>
                  <div className={styles.stat}>
                      <span className={styles.value}>{todaySummary.uniqueGames}</span>
                      <span className={styles.label}>Games</span>
                  </div>
                  {(todaySummary as any).sessions &&
                <div className={styles.stat}>
                    <span className={styles.value}>{(todaySummary as any).sessions}</span>
                    <span className={styles.label}>Sessions</span>
                </div>
                  }
              </div>
          </div>
                    }
                </div>
            </AnimateHeight>
        </div>
    );
};